/**
 * CR459: Assigned Sources Section — shown in Edit Adapter modal.
 * Lets admins view sources currently mapped to this adapter,
 * search + assign new ones, and unassign existing ones.
 */
import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ExternalLink, RefreshCw, Unlink, Plus, Copy } from "lucide-react";
import { useAdapterSources, type AdapterSourceRow } from "./useAdapterSources";
import { useToast } from "@/hooks/use-toast";

interface Props {
  adapterKey: string;
  defaultParams: unknown | null;
  /** Navigate to source editor — deep-link provided by parent */
  onOpenSource?: (sourceId: string) => void;
}

function SourceStatusBadge({ status }: { status: string | null }) {
  if (status === "active") {
    return <Badge variant="default" className="text-[10px] h-5">Active</Badge>;
  }
  return <Badge variant="outline" className="text-muted-foreground text-[10px] h-5">{status ?? "—"}</Badge>;
}

export function AssignedSourcesSection({ adapterKey, defaultParams, onOpenSource }: Props) {
  const { toast } = useToast();
  const { assignedSources, eligibleSources, loading, fetchAll, bulkAssign, unassign } =
    useAdapterSources(adapterKey);

  // Picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [assigning, setAssigning] = useState(false);

  // Unassign confirm
  const [unassignTarget, setUnassignTarget] = useState<AdapterSourceRow | null>(null);
  const [unassigning, setUnassigning] = useState(false);

  // Load on mount
  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapterKey]);

  // Filtered eligible sources for picker
  const filteredEligible = useMemo(() => {
    const q = pickerSearch.toLowerCase();
    return eligibleSources.filter(
      (s) =>
        !q ||
        (s.merchant_id ?? "").toLowerCase().includes(q) ||
        (s.merchant_name ?? "").toLowerCase().includes(q) ||
        (s.source_url ?? "").toLowerCase().includes(q)
    );
  }, [eligibleSources, pickerSearch]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAssign = async () => {
    setAssigning(true);
    const ok = await bulkAssign(Array.from(selectedIds), defaultParams);
    setAssigning(false);
    if (ok) {
      setPickerOpen(false);
      setSelectedIds(new Set());
      setPickerSearch("");
    }
  };

  const handleUnassign = async () => {
    if (!unassignTarget?.merchant_source_id) return;
    setUnassigning(true);
    await unassign(unassignTarget.merchant_source_id);
    setUnassigning(false);
    setUnassignTarget(null);
  };

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast({ title: "URL copied" });
  };

  return (
    <div className="space-y-3">
      <Separator />

      {/* Section header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Assigned Sources</span>
          <Badge variant="secondary" className="text-xs">
            {loading ? "…" : assignedSources.length}
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAll}
            disabled={loading}
            className="h-7 px-2"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => { setPickerOpen((v) => !v); setPickerSearch(""); setSelectedIds(new Set()); }}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Assign Sources
          </Button>
        </div>
      </div>

      {/* Assigned sources table */}
      {assignedSources.length === 0 && !loading ? (
        <p className="text-xs text-muted-foreground py-2">
          No sources assigned to this adapter yet.
        </p>
      ) : (
        <div className="rounded-md border text-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Merchant</TableHead>
                <TableHead className="text-xs">Source URL</TableHead>
                <TableHead className="text-xs w-[70px]">Status</TableHead>
                <TableHead className="text-xs w-[90px]">Capture</TableHead>
                <TableHead className="text-xs w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-4 text-muted-foreground text-xs">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : (
                assignedSources.map((s) => (
                  <TableRow key={s.merchant_source_id ?? s.merchant_id}>
                    <TableCell className="py-2">
                      <div className="font-medium text-xs">{s.merchant_name || s.merchant_id}</div>
                      <div className="text-[10px] text-muted-foreground">{s.merchant_id}</div>
                    </TableCell>
                    <TableCell className="py-2 max-w-[180px]">
                      <div className="flex items-center gap-1">
                        <span
                          className="text-[11px] text-muted-foreground truncate max-w-[150px]"
                          title={s.source_url ?? ""}
                        >
                          {s.source_url || "—"}
                        </span>
                        {s.source_url && (
                          <button
                            className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                            onClick={() => copyUrl(s.source_url!)}
                            title="Copy URL"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      <SourceStatusBadge status={s.status} />
                    </TableCell>
                    <TableCell className="py-2">
                      {s.flyer_image_capture_enabled ? (
                        <Badge variant="default" className="text-[10px] h-5">ON</Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground text-[10px] h-5">OFF</Badge>
                      )}
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex items-center gap-1">
                        {onOpenSource && s.merchant_source_id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            title="Open source editor"
                            onClick={() => onOpenSource(s.merchant_source_id!)}
                          >
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-destructive hover:text-destructive"
                          title="Unassign source"
                          onClick={() => setUnassignTarget(s)}
                        >
                          <Unlink className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Assign picker — inline expandable */}
      {pickerOpen && (
        <div className="rounded-md border p-3 space-y-3 bg-muted/30">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-foreground">
              Select FLYER sources to assign
            </span>
            {selectedIds.size > 0 && (
              <span className="text-xs text-muted-foreground">
                {selectedIds.size} selected
              </span>
            )}
          </div>

          <Input
            placeholder="Search by merchant or URL…"
            value={pickerSearch}
            onChange={(e) => setPickerSearch(e.target.value)}
            className="h-7 text-xs"
          />

          <div className="rounded-md border overflow-hidden max-h-[240px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[32px] text-xs" />
                  <TableHead className="text-xs">Merchant</TableHead>
                  <TableHead className="text-xs">Source URL</TableHead>
                  <TableHead className="text-xs w-[80px]">Current Adapter</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEligible.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-4 text-xs text-muted-foreground">
                      {pickerSearch ? "No sources match your search." : "All FLYER sources are already assigned to this adapter."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEligible.map((s) => {
                    const id = s.merchant_source_id ?? "";
                    return (
                      <TableRow
                        key={id}
                        className="cursor-pointer hover:bg-muted/40"
                        onClick={() => id && toggleSelect(id)}
                      >
                        <TableCell className="py-2 pl-3">
                          <Checkbox
                            checked={selectedIds.has(id)}
                            onCheckedChange={() => id && toggleSelect(id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell className="py-2">
                          <div className="text-xs font-medium">{s.merchant_name || s.merchant_id}</div>
                          <div className="text-[10px] text-muted-foreground">{s.merchant_id}</div>
                        </TableCell>
                        <TableCell className="py-2 max-w-[160px]">
                          <span
                            className="text-[11px] text-muted-foreground truncate block max-w-[155px]"
                            title={s.source_url ?? ""}
                          >
                            {s.source_url || "—"}
                          </span>
                        </TableCell>
                        <TableCell className="py-2">
                          {s.flyer_image_capture_adapter_key ? (
                            <span className="text-[10px] font-mono text-muted-foreground">
                              {s.flyer_image_capture_adapter_key}
                            </span>
                          ) : (
                            <span className="text-[10px] text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => { setPickerOpen(false); setSelectedIds(new Set()); setPickerSearch(""); }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-7 text-xs"
              disabled={selectedIds.size === 0 || assigning}
              onClick={handleAssign}
            >
              {assigning ? "Assigning…" : `Assign ${selectedIds.size > 0 ? selectedIds.size : ""} Source${selectedIds.size === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      )}

      {/* Unassign confirm dialog */}
      <AlertDialog open={!!unassignTarget} onOpenChange={(v) => !v && setUnassignTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Unassign "{unassignTarget?.merchant_name || unassignTarget?.merchant_id}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will clear the adapter mapping and disable image capture for this source.
              Existing adapter params are kept (not deleted).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unassigning}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={unassigning}
              onClick={handleUnassign}
            >
              {unassigning ? "Unassigning…" : "Unassign"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
